/**
 * shellIncidentReporter — critical-failure incident channel for shell/wizard.
 *
 * Design goals:
 *   - Fires only for warn/error-class events so production noise stays zero.
 *   - Structured incident payload: event, flowId, shopId, route, stage,
 *     errorCode, status, durationMs, ts.
 *   - Deep redaction of sensitive keys (same set as shellLogger).
 *   - Deduplication: the same (flowId + event + errorCode) combination is
 *     suppressed within a 30-second window to prevent alert spam on retries.
 *   - In-process buffer `window.__KEA_INCIDENTS` for unit / E2E inspection
 *     and for a future transport hook (monitoring / alerting).
 *   - Transport hook: callers may register a custom emitter via
 *     `shellIncidentReporter.setTransport(fn)`.  The default transport is a
 *     no-op so that incidents never make network calls without explicit
 *     opt-in (OWASP: no unintended data exfiltration).
 *
 * Usage:
 *   import { shellIncidentReporter } from './shellIncidentReporter';
 *
 *   shellIncidentReporter.report('bootstrap.degraded', {
 *     flowId: 'fl-abc123',
 *     shopId: null,
 *     stage: 'bootstrap',
 *     errorCode: 'DEGRADED',
 *     status: 'degraded',
 *     durationMs: 1240,
 *   });
 */

import { redactFields } from './shellLogger';

// ---------------------------------------------------------------------------
// Deduplication window
// ---------------------------------------------------------------------------

const DEDUP_WINDOW_MS = 30_000;

/**
 * Map of `dedupKey → tsMs` for incidents emitted in the current window.
 * Cleared automatically — entries expire silently via timestamp comparison.
 */
const dedupRegistry = new Map();

/**
 * Build the dedup key for a given incident.
 * Two incidents are considered identical when they share the same
 * flowId + event + errorCode within the 30-second window.
 *
 * @param {string} event
 * @param {object} fields
 * @returns {string}
 */
function buildDedupKey(event, fields) {
  return `${fields.flowId ?? ''}::${event}::${fields.errorCode ?? ''}`;
}

/**
 * Returns true when the incident is a duplicate of one emitted within
 * the last DEDUP_WINDOW_MS milliseconds.
 *
 * @param {string} key
 * @returns {boolean}
 */
function isDuplicate(key) {
  if (!dedupRegistry.has(key)) return false;
  const lastTs = dedupRegistry.get(key);
  return Date.now() - lastTs < DEDUP_WINDOW_MS;
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

/** Default transport is a no-op. Replace via setTransport() for monitoring. */
let transport = null;

// ---------------------------------------------------------------------------
// Core reporter
// ---------------------------------------------------------------------------

/**
 * Report a critical incident.
 *
 * Fields:
 *   event      {string}  — dot-notation event name, e.g. 'bootstrap.degraded'
 *   flowId     {string|null}
 *   shopId     {string|null}
 *   route      {string|null}
 *   stage      {string}  — 'bootstrap' | 'wizard' | 'bridge'
 *   errorCode  {string|null}
 *   status     {string|null}
 *   durationMs {number|null}
 *
 * @param {string} event
 * @param {object} [fields]
 */
function report(event, fields) {
  if (!event) return;

  const key = buildDedupKey(event, fields || {});
  if (isDuplicate(key)) return;

  // Register before emitting to guard against re-entrant calls.
  dedupRegistry.set(key, Date.now());

  const incident = {
    ts: Date.now(),
    event,
    ...redactFields(fields || {}),
  };

  // Write to in-process buffer — available to E2E and unit tests.
  if (typeof window !== 'undefined') {
    if (!Array.isArray(window.__KEA_INCIDENTS)) window.__KEA_INCIDENTS = [];
    window.__KEA_INCIDENTS.push(incident);
  }

  // Call custom transport when registered.
  if (typeof transport === 'function') {
    try {
      transport(incident);
    } catch (_err) {
      // Transport errors must never break the product flow.
    }
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const shellIncidentReporter = {
  /**
   * Report a critical incident.
   * @param {string} event
   * @param {object} [fields]
   */
  report,

  /**
   * Register a custom transport function called with each new (non-deduplicated)
   * incident.  Pass `null` to unregister.
   *
   * @param {function|null} fn - Called as fn(incident).  Must not throw.
   */
  setTransport(fn) {
    transport = typeof fn === 'function' ? fn : null;
  },

  /**
   * Reset deduplication state.
   * Call between test cases to prevent cross-test bleeding.
   */
  _resetDedupForTesting() {
    dedupRegistry.clear();
  },
};
